import { IPhotoSize } from "types/types";
import * as yup from 'yup';
import { FieldError, Merge, FieldErrorsImpl } from 'react-hook-form';
import { ELanguageCode } from "enum/ELanguageCode";

const BASE = "https://images.openfoodfacts.org/images/products";

export type RHFMaybeFieldError =
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | undefined

export const toFieldError = (err: RHFMaybeFieldError): FieldError | undefined => {
    const e = err as unknown as { type?: unknown } | undefined
    if (!e || typeof e !== 'object') return undefined
    return 'type' in e ? (err as FieldError) : undefined
}

export const enumToLabelValueList = (enumObj: Record<string, string>) => {
    return Object.entries(enumObj).map(([key, value]) => ({
        key: key,
        label: toTitleCase(value),
        value: value,
    }));
};

export const isValidEmail = (email: string) => {
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailPattern.test(email);
};

export const stringToArray = (str: string): string[] => {
    return str
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);
}

export const stringToDate = (dateString: string): Date | null => {
    if (!dateString) return null;

    if (dateString.includes('-')) {
        const [year, month, day] = dateString.split('-').map(Number);
        if (!year || !month || !day) return null;
        return new Date(Date.UTC(year, month - 1, day));
    }

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const cleaned = dateString.replace(',', '');
    const [monthStr, dayStr, yearStr] = cleaned.split(' ');

    const month = months.indexOf(monthStr.substring(0, 3));
    if (month === -1) return null;

    const day = parseInt(dayStr, 10);
    const year = Number(yearStr);
    if (isNaN(day) || isNaN(year)) return null;

    return new Date(Date.UTC(year, month, day));
};

export function dateToString(date: Date): string {
    const months = [
        'Jan', 'Feb', 'Mar', 'Apr',
        'May', 'Jun', 'Jul', 'Aug',
        'Sep', 'Oct', 'Nov', 'Dec'
    ];

    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();

    return `${months[month]} ${day}, ${year}`;
}

export const listToStringList = (list: string[]): string => {
    if (Array.isArray(list) && list?.length > 0) {
        return list.join(', ');
    }
    return ''
}

export const enumToLabelValueListExp = (
    enumArray: { id: number | string; name: string }[]
) => {
    return enumArray.map(({ id, name }) => ({
        key: String(id),
        label: String(name),
        value: String(id),
    }));
};

export function formatPhoneNumber(phoneNumberString = '') {
    const curchr = phoneNumberString.length;
    let curval = phoneNumberString;
    if (curchr === 3 && curval.indexOf('(') <= -1) {
        curval = '(' + curval + ')' + '-';
    } else if (curchr === 4 && curval.indexOf('(') > -1) {
        curval = curval + ')-';
    } else if (curchr === 5 && curval.indexOf(')') > -1) {
        curval = curval + '-';
    } else if (curchr === 9) {
        curval = curval + '-';
    }
    return curval;
}

export function clearFormatedPhoneNumber(formatedPhoneNumber: any) {
    const cleaned = ('' + formatedPhoneNumber).replace(/\D/g, '');
    return cleaned;
}

export function getAge(birthDate: any) {
    if (birthDate !== undefined) {
        const date = birthDate && birthDate.split('-');

        const year = new Date().getFullYear() - new Date(date).getFullYear();
        const month = new Date().getMonth() + 1 - (new Date(date).getMonth() + 1);
        const day = new Date().getDate() - new Date(date).getDate();

        if (year > 0) {
            return `${year}y`;
        } else if (month > 0) {
            return `${month}m`;
        } else {
            return `${day}d`;
        }
    }
}


export const userTheme = () => {
    if (
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
        localStorage.setItem('Theme', 'darkTheme');
    } else if (
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: light)').matches
    ) {
        localStorage.setItem('Theme', 'lightTheme');
    }
};

export const hexWithOpacity = (hex: string, opacity: number) =>
    hex + Math.round(opacity * 255).toString(16).padStart(2, '0');

export function toTitleCase(str: string) {
    if (!str) return '';
    return str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
        .split('_')
        .join(' ');
}

export const removeDuplicates = (list: any[], filterKey: string) => {
    if (!list) return [];
    const seen = new Set();

    return list.filter(item => {
        if (seen.has(item[filterKey])) return false;
        seen.add(item[filterKey]);
        return true;
    });

}

export const getErrorMessage = (error: any) => {
    return error?.response?.data?.message ?? error.message ?? '';
}

export const urlToPhotoSize = (url: string): IPhotoSize => {
    return {
        small: url,
        medium: url,
        large: url,
    }
}

export const formatDateTime = (
    createdAt?: string,
    language: string = 'en',
): string => {
    if (!createdAt) return '';

    try {
        const date = new Date(createdAt);

        // fallback if invalid date
        if (isNaN(date.getTime())) return '';

        const locale = 'en-GB';

        const formatted = date.toLocaleString(locale, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });

        return formatted.replace(',', ' |');
    } catch {
        return '';
    }
};

export function barcodePath(code: string) {
    return `${code.slice(0, 3)}/${code.slice(3, 6)}/${code.slice(6, 9)}/${code.slice(9)}`;
}

export function extractPhotos(product: { code: string; images?: Record<string, any> }): IPhotoSize[] {
    const path = `${BASE}/${barcodePath(product.code)}`;
    const images = product.images ?? {};

    return Object.keys(images)
        .filter((key) => /^\d+$/.test(key)) // only real uploaded photos
        .map((key) => ({
            small: images[key]?.sizes?.["100"] ? `${path}/${key}.100.jpg` : null,
            medium: images[key]?.sizes?.["400"] ? `${path}/${key}.400.jpg` : null,
            large: images[key]?.sizes?.full ? `${path}/${key}.jpg` : null,
        }));
}

export const getTranslations = (translationObject: any) => {
    const ar = translationObject.filter((tr: any) => tr.languageCode === ELanguageCode.AR)[0]
    const en = translationObject.filter((tr: any) => tr.languageCode === ELanguageCode.EN)[0]

    return { ar, en };
}
// Copying a value a reviewer needs elsewhere - a barcode they are about to
// paste into a search. navigator.clipboard exists only in a secure context, so
// over plain http on a LAN address the hidden textarea is the whole of it
// rather than a fallback nobody reaches.
const legacyCopy = (value: string) => {
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();

  try {
    return document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
};

export const copyToClipboard = async (value: string): Promise<boolean> => {
  if (!value) return false;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // The secure path can be refused - by permission, or by the window not
    // being the one in front - and the textarea below still works.
  }

  return legacyCopy(value);
};
